import React from 'react';
import { nowAsWallClockMs } from '../../../utils/eventDate';
import {
  FiUpload,
  FiPlus,
  FiLoader,
  FiPlay,
  FiMapPin,
  FiUser,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';

interface ProjectRunOfShowTabProps {
  handleExportRunOfShow: () => void;
  isGuestUser: boolean;
  openAddScheduleModal: () => void;
  isScheduleLoading: boolean;
  projectScheduleSorted: any[];
  scheduleGroupedByDate: any[];
  formatScheduleDuration: (start: string, end: string) => string;
  formatTimeOnly: (datetime: string, tz?: string) => string;
  formatTimezoneName: (datetime: string, tz?: string) => string;
  canUserUpdateScheduleEntry: (entry: any) => boolean;
  canUserDeleteScheduleEntry: (entry: any) => boolean;
  openEditScheduleModal: (entry: any) => void;
  promptDeleteScheduleEntry: (entry: any) => void;
}

export const ProjectRunOfShowTab: React.FC<ProjectRunOfShowTabProps> = ({
  handleExportRunOfShow,
  isGuestUser,
  openAddScheduleModal,
  isScheduleLoading,
  projectScheduleSorted,
  scheduleGroupedByDate,
  formatScheduleDuration,
  formatTimeOnly,
  formatTimezoneName,
  canUserUpdateScheduleEntry,
  canUserDeleteScheduleEntry,
  openEditScheduleModal,
  promptDeleteScheduleEntry,
}) => {
  return (
    <div className="space-y-6">
      {/* Header Row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-white text-base font-bold">Event Day Schedule</h3>
        <div className="flex items-center gap-2.5 ml-auto">
          <button
            onClick={handleExportRunOfShow}
            className="px-4 py-2 bg-white hover:bg-zinc-100 text-black rounded-full text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
          >
            <FiUpload className="w-3.5 h-3.5" />
            <span>Export</span>
          </button>
          {!isGuestUser && (
            <button
              onClick={openAddScheduleModal}
              className="px-4 py-2 bg-black hover:bg-zinc-900 text-white rounded-full text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Add Entry</span>
            </button>
          )}
        </div>
      </div>

      {isScheduleLoading ? (
        <div className="flex items-center justify-center py-24">
          <FiLoader className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : projectScheduleSorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 border border-white/5 bg-[#0f0f0f]/30 rounded-3xl text-center">
          <FiPlay className="w-12 h-12 text-zinc-700 mb-3" />
          <p className="text-zinc-400 text-sm font-semibold">No schedule entries yet.</p>
          {!isGuestUser && (
            <button
              onClick={openAddScheduleModal}
              className="mt-4 px-4 py-2 border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Add Schedule Entry</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {scheduleGroupedByDate.map((group) => (
            <div key={group.dateKey} className="space-y-6">
              {scheduleGroupedByDate.length > 1 && (
                <h4 className="text-zinc-400 text-xs font-bold uppercase tracking-wider">
                  {group.dateLabel}
                </h4>
              )}

              {/* Schedule Timeline */}
              <div className="relative space-y-6">
                {group.entries.map((entry: any, index: number) => {
                  const hasStarted =
                    new Date(entry.start_datetime).getTime() <=
                    nowAsWallClockMs(entry.iana_timezone);
                  const assigneeName =
                    entry.assignees && entry.assignees.length > 0
                      ? entry.assignees
                          .map((a: any) => a.full_name || a.username || a.email || 'Unknown')
                          .join(', ')
                      : 'Unassigned';
                  const duration = formatScheduleDuration(entry.start_datetime, entry.end_datetime);

                  return (
                    <div
                      key={entry.uuid}
                      className="relative group flex items-start gap-4 pl-0 sm:pl-28"
                    >
                      {/* Time Label */}
                      <div className="hidden sm:block absolute left-0 w-24 text-right pr-4">
                        <p className="text-blue-400 text-xs font-bold mt-3.5">
                          {formatTimeOnly(entry.start_datetime, entry.iana_timezone)}
                        </p>
                        {entry.iana_timezone && (
                          <p className="text-zinc-600 text-[9px] mt-0.5 leading-tight">
                            {formatTimezoneName(entry.start_datetime, entry.iana_timezone)}
                          </p>
                        )}
                        {duration && <p className="text-zinc-600 text-[10px] mt-0.5">{duration}</p>}
                      </div>

                      {/* Timeline node column */}
                      <div className="relative self-stretch shrink-0 flex flex-col items-center">
                        <div className="absolute left-1/2 -translate-x-1/2 top-0 -bottom-6 w-[1px] flex flex-col items-center">
                          <div
                            className={`w-[1px] h-[26px] bg-zinc-800/80 shrink-0 ${index === 0 ? 'opacity-0' : ''}`}
                          />
                          <div
                            className={`w-[1px] flex-grow bg-zinc-800/80 ${index === group.entries.length - 1 ? 'opacity-0' : ''}`}
                          />
                        </div>

                        {/* Timeline Node dot */}
                        <div className="relative z-10 w-9 h-9 rounded-full bg-[#0B0B0B] border border-white/5 flex items-center justify-center shrink-0 mt-2">
                          {hasStarted ? (
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-blue-500/20" />
                          ) : (
                            <span className="w-2.5 h-2.5 rounded-full border-2 border-zinc-600" />
                          )}
                        </div>
                      </div>

                      {/* Content Card */}
                      <div className="flex-1 bg-[#0F0F0F] border border-white/5 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3 hover:border-white/10 transition-all">
                        <div className="min-w-0">
                          {/* Inline time for mobile */}
                          <span className="sm:hidden text-blue-400 text-[10px] font-bold block">
                            {formatTimeOnly(entry.start_datetime, entry.iana_timezone)}
                            {duration ? ` · ${duration}` : ''}
                          </span>
                          {entry.iana_timezone && (
                            <span className="sm:hidden text-zinc-600 text-[9px] block mb-1">
                              {formatTimezoneName(entry.start_datetime, entry.iana_timezone)}
                            </span>
                          )}
                          <h4 className="text-white text-sm font-bold">{entry.title}</h4>
                          {entry.location && (
                            <p className="flex items-center gap-1.5 text-zinc-500 text-xs mt-1">
                              <FiMapPin className="w-3 h-3" />
                              {entry.location}
                            </p>
                          )}
                          {entry.notes && (
                            <p className="text-zinc-550 text-xs mt-1 leading-relaxed">
                              {entry.notes}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0">
                          <span className="flex items-center gap-1.5 px-3 py-1 bg-white/5 border border-white/5 rounded-full text-[10px] font-semibold text-zinc-300 whitespace-nowrap">
                            <FiUser className="w-3 h-3" />
                            {assigneeName}
                          </span>
                          {(canUserUpdateScheduleEntry(entry) ||
                            canUserDeleteScheduleEntry(entry)) && (
                            <div className="flex items-center gap-1.5">
                              {canUserUpdateScheduleEntry(entry) && (
                                <button
                                  onClick={() => openEditScheduleModal(entry)}
                                  className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors cursor-pointer"
                                >
                                  <FiEdit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {canUserDeleteScheduleEntry(entry) && (
                                <button
                                  onClick={() => promptDeleteScheduleEntry(entry)}
                                  className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-rose-500 transition-colors cursor-pointer"
                                >
                                  <FiTrash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
